import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from '@digital-twins/design-system/icons'

import { cn } from '@/lib/utils'

/**
 * The one surface the thread's evidence lives on.
 *
 * The sources list and an opened source used to be two panels that replaced
 * each other, each with its own shell, width and border — following a citation
 * swapped the whole right-hand side for a differently sized panel, which reads
 * as leaving the pane rather than looking closer inside it. This owns the shell
 * once: one hairline against the window edge, one width, one open animation.
 * What changes is only what is rendered inside it.
 *
 * Docked, it is an inset panel: a bordered card at the standard corner radius,
 * held off the window edge by a gutter, at a constant width — opening slides it
 * out from under the edge rather than squeezing its contents from zero, since
 * text rewrapping on every frame is what made the old open stutter. Below the
 * dock breakpoint the available thread area cannot hold two readable columns, so it covers
 * the thread as a full-screen sheet instead.
 */

/**
 * Gap between the panel and the window edge, and between the panel and the
 * conversation. The wrapper reserves one on each side, so the panel keeps its
 * inset at rest and still clears the viewport entirely when it is shut.
 */
const GUTTER_PX = 8

const WIDTH_STORAGE_KEY = 'thread-sources-drawer-width'
/** Below this the available thread area cannot hold two readable columns. */
const DOCK_MIN_WIDTH_PX = 768
const MIN_WIDTH_PX = 360
const DEFAULT_WIDTH_PX = 440
/** Never let the drawer squeeze the conversation below a readable column. */
const MAX_WIDTH_FRACTION = 0.6
/** A study frame opens at half the thread area, which sits inside the 60% cap. */
const STUDY_WIDTH_FRACTION = 0.5
const KEYBOARD_STEP_PX = 32

/**
 * How long the drawer takes to open or shut. The clip and the slide share this
 * duration and easing, so the wrapper's right edge and the drawer's left edge
 * travel together; give them different curves and a sliver of page background
 * runs down the opening edge for the length of the animation.
 */
export const DRAWER_TRANSITION_MS = 300

function clampWidth(value: number, availableWidth: number): number {
  const maximum = Math.max(MIN_WIDTH_PX, availableWidth * MAX_WIDTH_FRACTION)
  return Math.min(Math.max(value, MIN_WIDTH_PX), maximum)
}

// localStorage is undefined in this repo's jsdom test setup and throws in
// private browsing / storage-disabled contexts, so every read and write is
// wrapped and falls back to the default width rather than crashing on mount.
// Mirrors readPersistedCompositionPaneWidth in QuickStudyPage/quickStudyPure.ts.
function readStoredWidth(): number {
  if (typeof window === 'undefined') {
    return DEFAULT_WIDTH_PX
  }
  try {
    const stored = Number(window.localStorage.getItem(WIDTH_STORAGE_KEY))
    return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_WIDTH_PX
  } catch {
    return DEFAULT_WIDTH_PX
  }
}

interface ThreadSourcesDrawerProps {
  /** Whether the drawer is on screen. Drives both the clip and the slide. */
  open: boolean
  /**
   * A study frame is showing. Docked width becomes half the thread area, still
   * inside the 60% cap. Drag-resize stays, but nothing writes the saved
   * Sources width (`thread-sources-drawer-width`) while this is set.
   */
  studyFrame?: boolean
  /** Escape, and the sheet's own control on a narrow viewport. */
  onClose: () => void
  children: React.ReactNode
}

export function ThreadSourcesDrawer(props: ThreadSourcesDrawerProps) {
  const { open, studyFrame = false, onClose, children } = props

  const { t } = useTranslation('app')

  const [sourcesWidth, setSourcesWidth] = useState(readStoredWidth)
  const [studyWidth, setStudyWidth] = useState(DEFAULT_WIDTH_PX)
  const [availableWidth, setAvailableWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const docked = availableWidth >= DOCK_MIN_WIDTH_PX
  const drawerRef = useRef<HTMLElement | null>(null)
  const width = clampWidth(studyFrame ? studyWidth : sourcesWidth, availableWidth)
  const widthRef = useRef(width)
  widthRef.current = width
  const studyFrameRef = useRef(false)
  studyFrameRef.current = studyFrame
  const wasStudyFrame = useRef(false)
  const studyDraggedRef = useRef(false)

  useLayoutEffect(() => {
    // This row already excludes the app sidebar. Observing it also responds
    // when the sidebar opens without any window resize.
    const row = containerRef.current?.parentElement
    if (!row) return
    setAvailableWidth(row.getBoundingClientRect().width)
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setAvailableWidth(entry.contentRect.width)
    })
    observer.observe(row)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (studyFrame && (!wasStudyFrame.current || !studyDraggedRef.current)) {
      studyDraggedRef.current = false
      setStudyWidth(clampWidth(availableWidth * STUDY_WIDTH_FRACTION, availableWidth))
    }
    wasStudyFrame.current = studyFrame
  }, [studyFrame, availableWidth])

  useEffect(() => {
    if (!open) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  const persistWidth = useCallback((next: number) => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      window.localStorage.setItem(WIDTH_STORAGE_KEY, String(Math.round(next)))
    } catch {
      /* private mode / storage disabled */
    }
  }, [])

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const drawer = drawerRef.current
    if (!drawer) {
      return
    }
    const right = drawer.getBoundingClientRect().right
    event.currentTarget.setPointerCapture(event.pointerId)

    const onMove = (move: PointerEvent) => {
      const next = clampWidth(right - move.clientX, availableWidth)
      if (studyFrameRef.current) {
        studyDraggedRef.current = true
        setStudyWidth(next)
        return
      }
      setSourcesWidth(next)
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (!studyFrameRef.current) {
        persistWidth(widthRef.current)
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const handleHandleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return
    }
    event.preventDefault()
    const delta =
      event.key === 'ArrowLeft' ? KEYBOARD_STEP_PX : -KEYBOARD_STEP_PX
    if (studyFrameRef.current) {
      studyDraggedRef.current = true
      setStudyWidth(clampWidth(widthRef.current + delta, availableWidth))
      return
    }
    const next = clampWidth(widthRef.current + delta, availableWidth)
    setSourcesWidth(next)
    persistWidth(next)
  }

  if (!docked) {
    if (!open) {
      return <div ref={containerRef} className="contents" />
    }
    return (
      <div ref={containerRef} className="contents">
      <aside
        className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-background"
        data-testid="thread-sources-drawer"
        data-docked="false"
        data-open="true"
        aria-label={t('threads.detail.citedSourcesAria')}
      >
        {/* Covering the thread, Escape is not a visible way out: the sheet
            needs a control of its own. Docked, the header's sources toggle is
            already that control, so there is nothing to repeat here. */}
        <div className="flex justify-end px-2 py-1.5">
          <button
            type="button"
            aria-label={t('threads.detail.closeSourcesAria')}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            data-testid="thread-sources-drawer-close"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        {children}
      </aside>
      </div>
    )
  }

  return (
    // `overflow-hidden` clips the panel while its width animates. The transform
    // below lives on the panel, never on this wrapper: a transformed ancestor
    // would become the containing block for anything `fixed` rendered inside,
    // which is how the narrow-viewport sheet used to get trapped.
    <div ref={containerRef} className="contents">
    <div
      className={cn(
        'relative shrink-0 self-stretch overflow-hidden',
        'transition-[width] duration-300 ease-out motion-reduce:transition-none',
      )}
      style={{ width: open ? width + GUTTER_PX * 2 : 0 }}
      data-testid="thread-sources-rail"
    >
      <aside
        ref={drawerRef}
        className={cn(
          'absolute inset-y-2 flex flex-col overflow-hidden',
          // One boundary, drawn once: the panel's own border. The resize grip
          // sits on top of the left edge rather than beside it, so there is no
          // second rule running down the opening edge.
          'rounded-lg border border-border bg-background',
          'transition-transform duration-300 ease-out motion-reduce:transition-none',
        )}
        style={{
          width,
          right: GUTTER_PX,
          // Shut, it clears its own width plus the gutter, so nothing of it is
          // left peeking at the window edge. Closed and open travel the same
          // curve as the clip above, which keeps the panel's leading edge from
          // ever running ahead of the space being made for it.
          transform: open
            ? undefined
            : `translateX(calc(100% + ${GUTTER_PX}px))`,
        }}
        data-testid="thread-sources-drawer"
        data-docked="true"
        data-open={open}
        aria-label={t('threads.detail.citedSourcesAria')}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('threads.detail.resizeSourcesAria')}
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onKeyDown={handleHandleKeyDown}
          className="absolute inset-y-0 left-0 z-10 w-2 cursor-col-resize rounded-l-lg bg-transparent transition-colors hover:bg-primary/20 focus-visible:bg-primary/30 focus-visible:outline-none"
          data-testid="thread-sources-drawer-resize"
        />
        {children}
      </aside>
    </div>
    </div>
  )
}
